import { useTranslate } from 'react-polyglot';

import type { QuestionAnalytics } from '../../../../utils/buildQuestionAnalytics';
import { getQuestionText } from '../../../../utils/getQuestionText';
import { ColumnScrollArea } from '../common/ColumnScrollArea.styled';
import { Column, Columns } from '../common/Columns.styled';
import { SectionTitle } from '../common/SectionTitle.styled';
import { QuestionCards } from './components/QuestionCards/QuestionCards';
import { QuestionResultChart } from './components/QuestionResultChart/QuestionResultChart';
import { QuestionUserResults } from './components/QuestionUserResults/QuestionUserResults';
import { TwoColumns } from './QuestionAnalyticsFullScreen.styled';

interface QuestionAnalyticsFullScreenProps {
  analytics: QuestionAnalytics;
}

/**
 * The same three sections spread across the stage instead of stacked behind a scroll, so
 * the host has the chart and the whole class in view at once. Open answers take two
 * columns rather than three: there, the list of texts is also the overall result.
 */
export const QuestionAnalyticsFullScreen = ({
  analytics,
}: QuestionAnalyticsFullScreenProps) => {
  const t = useTranslate();

  const { question, kind, isRandom, cards, chart } = analytics;

  const questionText = getQuestionText(question, isRandom, t);

  const summaryColumn = (
    <Column>
      <SectionTitle>{t('__new.play.analyticsResults.question.summary')}</SectionTitle>
      <QuestionCards cards={cards} isExpanded />
    </Column>
  );

  const resultColumn = (
    <Column>
      <SectionTitle>
        {t('__new.play.analyticsResults.question.overallResult')}
      </SectionTitle>

      <ColumnScrollArea>
        <QuestionResultChart
          chart={chart}
          questionText={questionText}
          isRating={kind === 'rating'}
          isExpanded
        />
      </ColumnScrollArea>
    </Column>
  );

  if (chart.kind === 'openAnswers') {
    return (
      <TwoColumns>
        {summaryColumn}
        {resultColumn}
      </TwoColumns>
    );
  }

  return (
    <Columns>
      {summaryColumn}
      {resultColumn}

      <Column>
        <QuestionUserResults analytics={analytics} isExpanded />
      </Column>
    </Columns>
  );
};
